import { Check, Search, X } from 'lucide-react';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { clientesApi } from '../../../services/api/clientes.api';
import { ClienteListItem } from '../../../types/cliente';
import { IconButton } from '../../atoms/icon-button/icon-button';
import { Input } from '../../atoms/input/input';
import { Spinner } from '../../atoms/spinner/spinner';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  selectedHints?: Array<{ id: string; nome: string; email: string }>;
  disabled?: boolean;
  hasError?: boolean;
  testIdPrefix?: string;
}

interface Option {
  id: string;
  nome: string;
  email: string;
}

const Wrapper = styled.div<{ $hasError?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.colors.danger : theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.bgSurface};
  padding: 10px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
`;

const Counter = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.bgMuted};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.textPrimary};

  button {
    background: transparent;
    border: none;
    padding: 0;
    color: ${({ theme }) => theme.colors.textMuted};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
  }
  button:hover {
    color: ${({ theme }) => theme.colors.danger};
  }
`;

const ResultsBox = styled.div`
  max-height: 220px;
  overflow-y: auto;
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
  padding-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ResultRow = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.bgMuted : 'transparent'};
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    background: ${({ theme }) => theme.colors.bgMuted};
  }

  svg {
    color: ${({ theme }) => theme.colors.brandPrimary};
  }

  div {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  }

  small {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const buyerName = (item: Pick<ClienteListItem, 'nome' | 'sobrenome'>): string =>
  `${item.nome ?? ''} ${item.sobrenome ?? ''}`.trim() || '—';

export const ClientesMultiSelect = ({
  value,
  onChange,
  selectedHints,
  disabled,
  hasError,
  testIdPrefix = 'clientes-multiselect',
}: Props) => {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cacheRef = useRef<Map<string, Option>>(new Map());

  // Pré-popula cache com hints (vindos do detail) — evita "?" na chip.
  useEffect(() => {
    selectedHints?.forEach((h) => cacheRef.current.set(h.id, h));
  }, [selectedHints]);

  // Debounce 300ms
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(search), 300);
    return () => window.clearTimeout(id);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    clientesApi
      .list({ search: debounced || undefined, page: 1, limit: 20 })
      .then((res) => {
        if (cancelled) return;
        const mapped: Option[] = (res.data ?? []).map((c) => ({
          id: c.id,
          nome: buyerName(c),
          email: c.email,
        }));
        mapped.forEach((m) => cacheRef.current.set(m.id, m));
        setResults(mapped);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Falha ao buscar clientes');
        setResults([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const selectedOptions = useMemo(
    () =>
      value.map<Option>(
        (id) =>
          cacheRef.current.get(id) ?? {
            id,
            nome: `Cliente ${id.slice(0, 8)}`,
            email: '',
          },
      ),
    [value],
  );

  const toggle = useCallback(
    (id: string) => {
      if (disabled) return;
      if (value.includes(id)) {
        onChange(value.filter((v) => v !== id));
      } else {
        onChange([...value, id]);
      }
    },
    [value, onChange, disabled],
  );

  return (
    <Wrapper $hasError={hasError} data-testid={`${testIdPrefix}-wrapper`}>
      <Input
        data-testid={`${testIdPrefix}-search`}
        placeholder="Buscar cliente por nome ou e-mail"
        startIcon={<Search size={14} />}
        value={search}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        disabled={disabled}
      />

      <Header>
        <Counter data-testid={`${testIdPrefix}-counter`}>
          {value.length} cliente{value.length === 1 ? '' : 's'} selecionado
          {value.length === 1 ? '' : 's'}
        </Counter>
        {value.length > 0 && !disabled && (
          <IconButton label="Limpar seleção" onClick={() => onChange([])}>
            <X size={14} />
          </IconButton>
        )}
      </Header>

      {value.length > 0 && (
        <Chips data-testid={`${testIdPrefix}-chips`}>
          {selectedOptions.map((opt) => (
            <Chip key={opt.id}>
              {opt.nome}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remover ${opt.nome}`}
                  onClick={() => toggle(opt.id)}
                  data-testid={`${testIdPrefix}-chip-remove-${opt.id}`}
                >
                  <X size={12} />
                </button>
              )}
            </Chip>
          ))}
        </Chips>
      )}

      <ResultsBox>
        {loading && (
          <StatusRow>
            <Spinner /> Buscando…
          </StatusRow>
        )}
        {!loading && error && <StatusRow>{error}</StatusRow>}
        {!loading && !error && results.length === 0 && (
          <StatusRow>Nenhum cliente encontrado.</StatusRow>
        )}
        {!loading &&
          !error &&
          results.map((opt) => {
            const selected = value.includes(opt.id);
            return (
              <ResultRow
                key={opt.id}
                type="button"
                $selected={selected}
                onClick={() => toggle(opt.id)}
                disabled={disabled}
                data-testid={`${testIdPrefix}-option-${opt.id}`}
              >
                {selected ? <Check size={14} /> : <span style={{ width: 14 }} />}
                <div>
                  <strong>{opt.nome}</strong>
                  <small>{opt.email}</small>
                </div>
              </ResultRow>
            );
          })}
      </ResultsBox>
    </Wrapper>
  );
};
